/**
 * StudioPortal — Phase 5a MVP for The Studio (Media Operating Portal).
 *
 * Single-page tab UI:
 *   • Resources  — book rooms (live, edit, podcast)
 *   • Equipment  — checkout / return kit
 *   • Bookings   — see who has what booked
 *
 * Designed for a media-team operator who walks in, picks a space, picks a
 * camera, and walks out. Everything is deliberately compact + scannable.
 */
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import afterLogin from '@/lib/afterLogin';
import ReportIssueButton from '@/components/ReportIssueButton';
import BroadcastDashboard from '@/components/broadcast/BroadcastDashboard';
import VideoEditor from '@/components/broadcast/VideoEditor';
import BroadcastTemplatesAdmin from '@/components/broadcast/BroadcastTemplatesAdmin';
import {
  Mic, Camera, Lightbulb, Computer, MonitorSpeaker, Plus, Calendar,
  Package, Clock, Loader2, CheckCircle2, X, Video, Headphones,
  Users, UserPlus, Shield, Trash2, CalendarCheck
} from 'lucide-react';

const Switch = ({ checked, onCheckedChange, ...props }) => (
  <button
    type="button" role="switch" aria-checked={checked}
    onClick={() => onCheckedChange?.(!checked)}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-slate-300'}`}
    {...props}
  >
    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
  </button>
);

const STUDIO_ROLE_META = {
  studio_admin:    { label: 'Studio Admin',    color: 'bg-violet-100 text-violet-700',   desc: 'Manage team, resources, equipment' },
  studio_manager:  { label: 'Manager',         color: 'bg-blue-100 text-blue-700',       desc: 'Approve bookings, manage equipment' },
  studio_operator: { label: 'Operator',        color: 'bg-emerald-100 text-emerald-700', desc: 'Book + checkout for themselves' },
  viewer:          { label: 'Viewer',          color: 'bg-slate-100 text-slate-700',     desc: 'Read-only' },
};

const RESOURCE_TYPE_META = {
  live_room:         { label: 'Live Room',         icon: Video,         color: 'bg-red-100 text-red-700' },
  edit_suite:        { label: 'Edit Suite',        icon: Computer,      color: 'bg-blue-100 text-blue-700' },
  podcast_booth:     { label: 'Podcast Booth',     icon: Headphones,    color: 'bg-purple-100 text-purple-700' },
  rehearsal_room:    { label: 'Rehearsal Room',    icon: Mic,           color: 'bg-amber-100 text-amber-700' },
  control_room:      { label: 'Control Room',      icon: MonitorSpeaker,color: 'bg-slate-100 text-slate-700' },
  green_room:        { label: 'Green Room',        icon: Package,       color: 'bg-emerald-100 text-emerald-700' },
  outside_broadcast: { label: 'Outside Broadcast', icon: Camera,        color: 'bg-indigo-100 text-indigo-700' },
};
const EQUIPMENT_CAT_META = {
  camera:         { label: 'Camera',         icon: Camera },
  microphone:     { label: 'Microphone',     icon: Mic },
  lighting:       { label: 'Lighting',       icon: Lightbulb },
  computer:       { label: 'Computer',       icon: Computer },
  audio_mixer:    { label: 'Audio Mixer',    icon: MonitorSpeaker },
  video_switcher: { label: 'Video Switcher', icon: Video },
  tripod:         { label: 'Tripod',         icon: Package },
  drone:          { label: 'Drone',          icon: Package },
  other:          { label: 'Other',          icon: Package },
};
const STATUS_BADGE = {
  available:      'bg-emerald-100 text-emerald-700',
  checked_out:    'bg-amber-100 text-amber-700',
  maintenance:    'bg-orange-100 text-orange-700',
  decommissioned: 'bg-slate-200 text-slate-600',
  lost:           'bg-red-100 text-red-700',
  pending:        'bg-amber-100 text-amber-700',
  confirmed:      'bg-emerald-100 text-emerald-700',
  completed:      'bg-slate-100 text-slate-600',
  cancelled:      'bg-red-100 text-red-700',
};

const StudioPortal = () => {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);

  // Access check — bounce non-members early with a helpful message
  const { data: me, isLoading: loadingMe, isError } = useQuery({
    queryKey: ['studio-me'],
    queryFn: () => api.get('/studio/team/me').then(r => r.data),
    retry: false,
  });

  if (loadingMe) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }
  if (isError || !me) {
    return (
      <div className="min-h-screen bg-slate-50 py-16">
        <div className="max-w-xl mx-auto px-4">
          <Card>
            <CardContent className="py-12 text-center">
              <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">No Studio Access</h2>
              <p className="text-slate-500 text-sm mb-6">
                You aren't on the Studio team yet. Ask a Studio admin to add you with a role
                (Operator / Manager) before you can see rooms, gear or bookings.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      await afterLogin({ navigate, userType: 'admin', tenantId: null, redirect: '' });
                    } catch {
                      navigate('/admin', { replace: true });
                    }
                  }}
                  data-testid="studio-go-to-admin"
                >
                  Go to Admin Dashboard
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => {
                    logout();
                    navigate('/studio/login', { replace: true });
                  }}
                  data-testid="studio-sign-out"
                >
                  Sign out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const canManageTeam = me.is_super || me.role === 'studio_admin';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="studio-portal">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Video className="h-6 w-6 text-violet-600" />
              The Studio
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Book spaces, sign out gear, see who has what.
            </p>
            <a href="/studio/book" target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 mt-1 text-xs text-violet-700 hover:underline" data-testid="studio-public-link">
              <CalendarCheck className="h-3 w-3" /> Public booking page → /studio/book
            </a>
          </div>
          <div className="flex items-center gap-2">
            <ReportIssueButton scopeType="studio" scopeLabel="The Studio" />
            {me.role && (
              <span className={`text-xs px-2 py-1 rounded border ${STUDIO_ROLE_META[me.role]?.color || 'bg-slate-100 text-slate-700'}`} data-testid="studio-my-role">
                {me.is_super ? 'Super Admin' : (STUDIO_ROLE_META[me.role]?.label || me.role)}
              </span>
            )}
          </div>
        </div>

        <Tabs defaultValue="resources" className="w-full">
          <TabsList className={`grid w-full ${canManageTeam ? 'max-w-5xl grid-cols-7' : 'max-w-4xl grid-cols-2 sm:grid-cols-3 lg:grid-cols-6'}`}>
            <TabsTrigger value="resources" data-testid="studio-tab-resources">Resources</TabsTrigger>
            <TabsTrigger value="equipment" data-testid="studio-tab-equipment">Equipment</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="studio-tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="broadcast" data-testid="studio-tab-broadcast">Broadcast</TabsTrigger>
            <TabsTrigger value="editor" data-testid="studio-tab-editor">Editor</TabsTrigger>
            <TabsTrigger value="templates" data-testid="studio-tab-templates">Templates</TabsTrigger>
            {canManageTeam && <TabsTrigger value="team" data-testid="studio-tab-team">Team</TabsTrigger>}
          </TabsList>
          <TabsContent value="resources" className="mt-4"><ResourcesTab me={me} /></TabsContent>
          <TabsContent value="equipment" className="mt-4"><EquipmentTab me={me} /></TabsContent>
          <TabsContent value="bookings" className="mt-4"><BookingsTab me={me} /></TabsContent>
          <TabsContent value="broadcast" className="mt-4">
            <BroadcastDashboard tenantId={useAuthStore.getState().user?.tenant_id || me?.tenant_id || 'global'} studioId="studio" />
          </TabsContent>
          <TabsContent value="editor" className="mt-4">
            <VideoEditor tenantId={useAuthStore.getState().user?.tenant_id || me?.tenant_id || 'global'} />
          </TabsContent>
          <TabsContent value="templates" className="mt-4">
            <BroadcastTemplatesAdmin tenantId={useAuthStore.getState().user?.tenant_id || me?.tenant_id || 'global'} />
          </TabsContent>
          {canManageTeam && <TabsContent value="team" className="mt-4"><StudioTeamTab /></TabsContent>}
        </Tabs>
      </div>
    </div>
  );
};


// ==============================================================
// RESOURCES TAB
// ==============================================================
const ResourcesTab = ({ me }) => {
  const canMutate = me?.is_super || ['studio_admin', 'studio_manager'].includes(me?.role);
  const canBook = me?.is_super || ['studio_admin', 'studio_manager', 'studio_operator'].includes(me?.role);
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [showBook, setShowBook] = useState(null);   // resource to book
  const [form, setForm] = useState({ name: '', type: 'live_room', capacity: '', hourly_rate: '0', is_public: false });

  const { data: resources = [], isLoading } = useQuery({
    queryKey: ['studio-resources'],
    queryFn: () => api.get('/studio/resources').then(r => r.data),
  });

  const addMutation = useMutation({
    mutationFn: (payload) => api.post('/studio/resources', payload),
    onSuccess: () => {
      toast.success('Resource added');
      queryClient.invalidateQueries({ queryKey: ['studio-resources'] });
      setShowAdd(false);
      setForm({ name: '', type: 'live_room', capacity: '', hourly_rate: '0', is_public: false });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to add'),
  });

  if (isLoading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canMutate && (
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="studio-add-resource">
            <Plus className="h-4 w-4 mr-1" /> Add Resource
          </Button>
        )}
      </div>

      {resources.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400 text-sm">
            <Video className="h-8 w-8 mx-auto mb-2 opacity-50" />
            No resources yet. Add a live room, edit suite or podcast booth to start.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {resources.map(r => {
            const meta = RESOURCE_TYPE_META[r.type] || RESOURCE_TYPE_META.live_room;
            const Icon = meta.icon;
            return (
              <Card key={r.id} data-testid={`studio-resource-${r.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${meta.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{r.name}</p>
                      <p className="text-xs text-slate-500">{meta.label}{r.capacity ? ` · ${r.capacity} pax` : ''}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className={`text-xs ${STATUS_BADGE[r.status] || ''}`}>{r.status}</Badge>
                        {r.hourly_rate > 0 && (
                          <span className="text-xs text-slate-500">{r.currency} {r.hourly_rate}/hr</span>
                        )}
                        {r.is_public && <Badge variant="outline" className="text-[10px] text-violet-700 border-violet-200 bg-violet-50" data-testid={`resource-public-badge-${r.id}`}>Public</Badge>}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm" className="w-full mt-3"
                    disabled={r.status !== 'available' || !canBook}
                    onClick={() => setShowBook(r)}
                    data-testid={`studio-book-${r.id}`}
                  >
                    <Calendar className="h-3.5 w-3.5 mr-1" /> Book
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Resource dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Resource</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Studio A" data-testid="resource-name" /></div>
            <div>
              <Label className="text-xs">Type</Label>
              <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="resource-type">
                {Object.entries(RESOURCE_TYPE_META).map(([k, m]) => (<option key={k} value={k}>{m.label}</option>))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Capacity</Label><Input type="number" value={form.capacity} onChange={e => setForm({...form, capacity: e.target.value})} placeholder="10" /></div>
              <div><Label className="text-xs">Hourly Rate (USD)</Label><Input type="number" value={form.hourly_rate} onChange={e => setForm({...form, hourly_rate: e.target.value})} placeholder="0" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm border-t pt-3">
              <input
                type="checkbox"
                checked={form.is_public}
                onChange={e => setForm({ ...form, is_public: e.target.checked })}
                className="rounded border-slate-300"
                data-testid="resource-is-public"
              />
              <span>
                <span className="font-medium">Open for public booking</span>
                <span className="text-xs text-slate-500 block">Allow external clients to request this room via <span className="font-mono">/studio/book</span></span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (!form.name.trim()) { toast.error('Name required'); return; }
                addMutation.mutate({
                  name: form.name.trim(), type: form.type,
                  capacity: form.capacity ? parseInt(form.capacity, 10) : null,
                  hourly_rate: parseFloat(form.hourly_rate) || 0,
                  is_public: form.is_public,
                });
              }}
              disabled={addMutation.isPending}
              data-testid="resource-submit"
            >
              {addMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Book dialog */}
      {showBook && <BookResourceDialog resource={showBook} onClose={() => setShowBook(null)} />}
    </div>
  );
};


const BookResourceDialog = ({ resource, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    start_at: '', end_at: '', purpose: '', notes: '',
  });

  const bookMutation = useMutation({
    mutationFn: (payload) => api.post('/studio/bookings', payload),
    onSuccess: (res) => {
      toast.success(`Booked — ${resource.name}, est. ${res.data.currency} ${res.data.estimated_cost}`);
      queryClient.invalidateQueries({ queryKey: ['studio-bookings'] });
      onClose();
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : detail;
      toast.error(msg || 'Booking failed');
    },
  });

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Book {resource.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Start</Label>
              <Input type="datetime-local" value={form.start_at} onChange={e => setForm({...form, start_at: e.target.value})} data-testid="booking-start" />
            </div>
            <div>
              <Label className="text-xs">End</Label>
              <Input type="datetime-local" value={form.end_at} onChange={e => setForm({...form, end_at: e.target.value})} data-testid="booking-end" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Purpose</Label>
            <Input value={form.purpose} onChange={e => setForm({...form, purpose: e.target.value})} placeholder="Sunday service livestream" data-testid="booking-purpose" />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (!form.start_at || !form.end_at) { toast.error('Pick a start and end'); return; }
              bookMutation.mutate({
                resource_id: resource.id,
                start_at: new Date(form.start_at).toISOString(),
                end_at: new Date(form.end_at).toISOString(),
                purpose: form.purpose || null,
                notes: form.notes || null,
              });
            }}
            disabled={bookMutation.isPending}
            data-testid="booking-submit"
          >
            {bookMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Book'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ==============================================================
// EQUIPMENT TAB
// ==============================================================
const EquipmentTab = ({ me }) => {
  const canMutate = me?.is_super || ['studio_admin', 'studio_manager'].includes(me?.role);
  const canCheckout = me?.is_super || ['studio_admin', 'studio_manager', 'studio_operator'].includes(me?.role);
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'camera', serial_no: '' });

  const { data: equipment = [], isLoading } = useQuery({
    queryKey: ['studio-equipment'],
    queryFn: () => api.get('/studio/equipment').then(r => r.data),
  });

  const addMutation = useMutation({
    mutationFn: (payload) => api.post('/studio/equipment', payload),
    onSuccess: () => {
      toast.success('Equipment added');
      queryClient.invalidateQueries({ queryKey: ['studio-equipment'] });
      setShowAdd(false);
      setForm({ name: '', category: 'camera', serial_no: '' });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to add'),
  });

  const checkoutMutation = useMutation({
    mutationFn: (equipmentId) => api.post('/studio/checkouts', { equipment_id: equipmentId }),
    onSuccess: () => {
      toast.success('Checked out');
      queryClient.invalidateQueries({ queryKey: ['studio-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['studio-checkouts'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to check out'),
  });

  const { data: openCheckouts = [] } = useQuery({
    queryKey: ['studio-checkouts', 'open'],
    queryFn: () => api.get('/studio/checkouts?open_only=true').then(r => r.data),
  });

  const returnMutation = useMutation({
    mutationFn: (checkoutId) => api.post(`/studio/checkouts/${checkoutId}/return`),
    onSuccess: () => {
      toast.success('Returned');
      queryClient.invalidateQueries({ queryKey: ['studio-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['studio-checkouts'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to return'),
  });

  // Map equipment_id -> checkout for quick "Return" button (must be called
  // before any early return to keep hook order stable).
  const openByEqId = useMemo(() => {
    const map = {};
    openCheckouts.forEach(c => { map[c.equipment_id] = c; });
    return map;
  }, [openCheckouts]);

  if (isLoading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canMutate && (
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="studio-add-equipment">
            <Plus className="h-4 w-4 mr-1" /> Add Equipment
          </Button>
        )}
      </div>

      {equipment.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400 text-sm">
            <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
            No equipment yet. Add cameras, mics, lights or computers to get started.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {equipment.map(e => {
            const meta = EQUIPMENT_CAT_META[e.category] || EQUIPMENT_CAT_META.other;
            const Icon = meta.icon;
            const openCo = openByEqId[e.id];
            return (
              <Card key={e.id} data-testid={`studio-equipment-${e.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{e.name}</p>
                      <p className="text-xs text-slate-500">{meta.label}{e.serial_no ? ` · ${e.serial_no}` : ''}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={`text-xs ${STATUS_BADGE[e.status] || ''}`}>{e.status}</Badge>
                        {e.current_holder_name && (
                          <span className="text-xs text-slate-500 truncate">with {e.current_holder_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {e.status === 'available' && canCheckout && (
                    <Button
                      size="sm" className="w-full mt-3"
                      onClick={() => checkoutMutation.mutate(e.id)}
                      disabled={checkoutMutation.isPending}
                      data-testid={`studio-checkout-${e.id}`}
                    >
                      Check Out
                    </Button>
                  )}
                  {e.status === 'checked_out' && openCo && canCheckout && (
                    <Button
                      size="sm" variant="outline" className="w-full mt-3"
                      onClick={() => returnMutation.mutate(openCo.id)}
                      disabled={returnMutation.isPending}
                      data-testid={`studio-return-${e.id}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark Returned
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Equipment dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Equipment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Canon EOS R5" data-testid="equipment-name" /></div>
            <div>
              <Label className="text-xs">Category</Label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="equipment-category">
                {Object.entries(EQUIPMENT_CAT_META).map(([k, m]) => (<option key={k} value={k}>{m.label}</option>))}
              </select>
            </div>
            <div><Label className="text-xs">Serial Number</Label><Input value={form.serial_no} onChange={e => setForm({...form, serial_no: e.target.value})} placeholder="SN-12345" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (!form.name.trim()) { toast.error('Name required'); return; }
                addMutation.mutate({
                  name: form.name.trim(), category: form.category,
                  serial_no: form.serial_no.trim() || null,
                });
              }}
              disabled={addMutation.isPending}
              data-testid="equipment-submit"
            >
              {addMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==============================================================
// BOOKINGS TAB
// ==============================================================
const BookingsTab = ({ me }) => {
  const canApprove = me?.is_super || ['studio_admin', 'studio_manager'].includes(me?.role);
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('upcoming');

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['studio-bookings', filter],
    queryFn: () => api.get(`/studio/bookings${filter === 'upcoming' ? '?upcoming=true' : ''}`).then(r => r.data),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => api.put(`/studio/bookings/${id}/status`, { new_status: status }),
    onSuccess: () => {
      toast.success('Updated');
      queryClient.invalidateQueries({ queryKey: ['studio-bookings'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  const fmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString();
  };

  if (isLoading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <select value={filter} onChange={e => setFilter(e.target.value)} className="text-sm border rounded h-8 px-2 bg-white" data-testid="bookings-filter">
          <option value="upcoming">Upcoming</option>
          <option value="all">All</option>
        </select>
      </div>

      {bookings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400 text-sm">
            <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
            No bookings yet.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {bookings.map(b => (
                <div key={b.id} className="p-3 flex items-center gap-3" data-testid={`booking-row-${b.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm truncate">{b.resource_name || b.resource_id}</p>
                      <Badge variant="outline" className={`text-xs ${STATUS_BADGE[b.status] || ''}`}>{b.status}</Badge>
                      {b.estimated_cost > 0 && <span className="text-xs text-slate-500">{b.currency} {b.estimated_cost}</span>}
                      {b.source === 'public_marketplace' && (
                        <Badge variant="outline" className="text-[10px] text-violet-700 border-violet-200 bg-violet-50" data-testid={`booking-marketplace-${b.id}`} title={`External booking · Ref ${b.booking_reference || ''}`}>
                          External · {b.booking_reference}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      <Clock className="h-3 w-3 inline mr-0.5" />
                      {fmt(b.start_at)} → {fmt(b.end_at)} ({b.hours}h) · {b.client_name || b.booked_by_name || b.booked_by_email}
                      {b.client_email && b.source === 'public_marketplace' && <span className="text-slate-400"> · {b.client_email}</span>}
                    </p>
                    {b.purpose && <p className="text-xs text-slate-600 mt-0.5">{b.purpose}</p>}
                  </div>
                  {b.status === 'pending' && canApprove && (
                    <>
                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => statusMutation.mutate({ id: b.id, status: 'confirmed' })} data-testid={`booking-confirm-${b.id}`}>
                        Confirm
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => statusMutation.mutate({ id: b.id, status: 'cancelled' })} data-testid={`booking-cancel-${b.id}`}>
                        <X className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                  {b.status === 'confirmed' && canApprove && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => statusMutation.mutate({ id: b.id, status: 'completed' })} data-testid={`booking-complete-${b.id}`}>
                      Complete
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==============================================================
// TEAM TAB
// ==============================================================
const StudioTeamTab = () => {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'studio_operator' });

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['studio-team'],
    queryFn: () => api.get('/studio/team').then(r => r.data),
  });

  const addMutation = useMutation({
    mutationFn: (payload) => api.post('/studio/team', payload),
    onSuccess: () => {
      toast.success('Team member added');
      queryClient.invalidateQueries({ queryKey: ['studio-team'] });
      setShowAdd(false);
      setForm({ email: '', name: '', password: '', role: 'studio_operator' });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to add'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ memberId, payload }) => api.put(`/studio/team/${memberId}`, payload),
    onSuccess: () => {
      toast.success('Updated');
      queryClient.invalidateQueries({ queryKey: ['studio-team'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  const removeMutation = useMutation({
    mutationFn: ({ memberId, hard }) => api.delete(`/studio/team/${memberId}${hard ? '?hard=true' : ''}`),
    onSuccess: () => {
      toast.success('Removed');
      queryClient.invalidateQueries({ queryKey: ['studio-team'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  if (isLoading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-3" data-testid="studio-team-tab">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-slate-500">
          Manage who can book rooms, sign out gear, and approve bookings.
        </p>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="studio-add-member">
          <UserPlus className="h-3.5 w-3.5 mr-1" /> Add Member
        </Button>
      </div>

      <div className="space-y-2">
        {members.length === 0 && (
          <div className="text-center py-8 border border-dashed rounded-lg text-slate-400 text-sm">
            <Users className="h-6 w-6 mx-auto mb-2 opacity-50" />
            No members yet. Add a Studio Admin or Operator.
          </div>
        )}
        {members.map(m => {
          const meta = STUDIO_ROLE_META[m.role] || STUDIO_ROLE_META.viewer;
          return (
            <Card key={m.id} className={m.is_active ? '' : 'opacity-60'} data-testid={`studio-team-row-${m.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                  <Shield className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium truncate">{m.name || m.email}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                    {!m.is_active && <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Disabled</span>}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{m.email}</p>
                </div>
                <select
                  value={m.role}
                  onChange={(e) => updateMutation.mutate({ memberId: m.id, payload: { role: e.target.value } })}
                  className="text-xs border rounded px-2 py-1 bg-white"
                  data-testid={`studio-team-role-${m.id}`}
                >
                  {Object.keys(STUDIO_ROLE_META).map(r => (<option key={r} value={r}>{STUDIO_ROLE_META[r].label}</option>))}
                </select>
                <Switch
                  checked={!!m.is_active}
                  onCheckedChange={(v) => updateMutation.mutate({ memberId: m.id, payload: { is_active: v } })}
                  data-testid={`studio-team-toggle-${m.id}`}
                />
                <Button
                  variant="ghost" size="sm"
                  onClick={() => {
                    if (window.confirm(`Remove ${m.email} from Studio team?`)) {
                      removeMutation.mutate({ memberId: m.id, hard: true });
                    }
                  }}
                  className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                  data-testid={`studio-team-delete-${m.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Role legend */}
      <div className="text-xs text-slate-500 space-y-1 pt-2 border-t">
        {Object.entries(STUDIO_ROLE_META).map(([r, meta]) => (
          <div key={r}>
            <span className={`inline-block px-1.5 py-0.5 rounded mr-1 ${meta.color}`}>{meta.label}</span>
            <span className="text-slate-400">{meta.desc}</span>
          </div>
        ))}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Studio Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="member@example.com" data-testid="studio-team-email" /></div>
            <div><Label className="text-xs">Name (optional)</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Full name" /></div>
            <div>
              <Label className="text-xs">Role</Label>
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="studio-team-add-role">
                {Object.keys(STUDIO_ROLE_META).map(r => (<option key={r} value={r}>{STUDIO_ROLE_META[r].label} — {STUDIO_ROLE_META[r].desc}</option>))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Password <span className="text-slate-400">(only if new user)</span></Label>
              <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Temporary password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (!form.email.trim()) { toast.error('Email required'); return; }
                addMutation.mutate({
                  email: form.email.trim().toLowerCase(),
                  name: form.name.trim() || undefined,
                  password: form.password || undefined,
                  role: form.role,
                });
              }}
              disabled={addMutation.isPending}
              data-testid="studio-team-submit"
            >
              {addMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default StudioPortal;
